import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

// Default configurations for each element type
export const VENUE_ELEMENT_CONFIGS = {
  stage: { label: 'STAGE', width: 200, height: 60, minWidth: 100, minHeight: 40 },
  dance_floor: { label: 'DANCE FLOOR', width: 150, height: 150, minWidth: 80, minHeight: 80 },
  bar: { label: 'BAR', width: 120, height: 30, minWidth: 60, minHeight: 20 },
  dj_booth: { label: 'DJ', width: 50, height: 50, minWidth: 30, minHeight: 30 },
  photo_booth: { label: 'PHOTO BOOTH', width: 60, height: 60, minWidth: 40, minHeight: 40 },
  entrance: { label: 'ENTRANCE', width: 80, height: 30, minWidth: 50, minHeight: 20 },
  cocktail_table: { label: 'COCKTAIL', width: 30, height: 30, minWidth: 20, minHeight: 20, isCircle: true },
  restrooms: { label: 'WC', width: 50, height: 50, minWidth: 30, minHeight: 30 },
};

// Resize handle component
function ResizeHandle({ position, onMouseDown }) {
  const cursorMap = {
    'nw': 'nw-resize', 'n': 'n-resize', 'ne': 'ne-resize',
    'w': 'w-resize', 'e': 'e-resize',
    'sw': 'sw-resize', 's': 's-resize', 'se': 'se-resize',
  };

  const positionStyles = {
    'nw': { top: -4, left: -4 },
    'n': { top: -4, left: '50%', transform: 'translateX(-50%)' },
    'ne': { top: -4, right: -4 },
    'w': { top: '50%', left: -4, transform: 'translateY(-50%)' },
    'e': { top: '50%', right: -4, transform: 'translateY(-50%)' },
    'sw': { bottom: -4, left: -4 },
    's': { bottom: -4, left: '50%', transform: 'translateX(-50%)' },
    'se': { bottom: -4, right: -4 },
  };

  return (
    <div
      className="absolute w-2 h-2 bg-white border border-slate-800 z-20"
      style={{
        ...positionStyles[position],
        cursor: cursorMap[position],
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        onMouseDown(e, position);
      }}
    />
  );
}

export default function VenueElementShape({
  element,
  zoom = 1,
  isSelected = false,
  onClick,
  onPositionChange,
  onSizeChange,
  onDuplicate,
  onDelete,
  gridSize = 20,
}) {
  const elementRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState(null);
  const [isRotating, setIsRotating] = useState(false);
  const [rotateStart, setRotateStart] = useState(null);

  const config = VENUE_ELEMENT_CONFIGS[element.element_type] || VENUE_ELEMENT_CONFIGS.stage;
  const width = element.width || config.width;
  const height = element.height || config.height;
  const isCircle = config.isCircle;
  const rotation = element.rotation || 0;

  // Drag functionality - CustomDragLayer handles the preview
  const [{ isDragging }, drag, preview] = useDrag({
    type: 'VENUE_ELEMENT',
    item: () => ({
      id: element.id,
      type: 'VENUE_ELEMENT',
      element_type: element.element_type,
      width,
      height,
      rotation: rotation,
    }),
    canDrag: !isResizing && !isRotating,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Hide default browser drag preview - CustomDragLayer handles it
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  // Snap to grid helper
  const snapToGrid = useCallback((value) => {
    return Math.round(value / gridSize) * gridSize;
  }, [gridSize]);

  // Handle rotation start
  const handleRotateStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsRotating(true);
    
    const rect = elementRef.current?.getBoundingClientRect();
    if (rect) {
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      setRotateStart({
        centerX,
        centerY,
        startAngle: Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI),
        initialRotation: rotation,
      });
    }
  }, [rotation]);

  // Handle rotation move
  const handleRotateMove = useCallback((e) => {
    if (!isRotating || !rotateStart) return;
    
    const { centerX, centerY, startAngle, initialRotation } = rotateStart;
    const currentAngle = Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);
    let newRotation = initialRotation + (currentAngle - startAngle);
    
    // Normalize to 0-360
    newRotation = ((newRotation % 360) + 360) % 360;
    
    // Snap to 15 degree increments if shift is held
    if (e.shiftKey) {
      newRotation = Math.round(newRotation / 15) * 15;
    }
    
    if (onSizeChange) {
      onSizeChange(element.id, { rotation: Math.round(newRotation) });
    }
  }, [isRotating, rotateStart, element.id, onSizeChange]);

  // Handle rotation end
  const handleRotateEnd = useCallback(() => {
    setIsRotating(false);
    setRotateStart(null);
  }, []);

  // Global mouse event listeners for rotation
  useEffect(() => {
    if (isRotating) {
      window.addEventListener('mousemove', handleRotateMove);
      window.addEventListener('mouseup', handleRotateEnd);
      return () => {
        window.removeEventListener('mousemove', handleRotateMove);
        window.removeEventListener('mouseup', handleRotateEnd);
      };
    }
  }, [isRotating, handleRotateMove, handleRotateEnd]);

  // Handle resize
  const handleResizeStart = useCallback((e, position) => {
    e.preventDefault();
    setIsResizing(true);
    setResizeStart({
      position,
      mouseX: e.clientX,
      mouseY: e.clientY,
      width,
      height,
      x: element.position_x,
      y: element.position_y,
    });
  }, [width, height, element.position_x, element.position_y]);

  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !resizeStart) return;

    const deltaX = (e.clientX - resizeStart.mouseX) / zoom;
    const deltaY = (e.clientY - resizeStart.mouseY) / zoom;

    let newWidth = resizeStart.width;
    let newHeight = resizeStart.height;
    let newX = resizeStart.x;
    let newY = resizeStart.y;

    const { position } = resizeStart;

    // Calculate new dimensions based on resize handle position
    if (position.includes('e')) {
      newWidth = Math.max(config.minWidth, resizeStart.width + deltaX);
    }
    if (position.includes('w')) {
      const widthDelta = Math.min(deltaX, resizeStart.width - config.minWidth);
      newWidth = resizeStart.width - widthDelta;
      newX = resizeStart.x + widthDelta;
    }
    if (position.includes('s')) {
      newHeight = Math.max(config.minHeight, resizeStart.height + deltaY);
    }
    if (position.includes('n')) {
      const heightDelta = Math.min(deltaY, resizeStart.height - config.minHeight);
      newHeight = resizeStart.height - heightDelta;
      newY = resizeStart.y + heightDelta;
    }

    // Snap to grid
    newWidth = snapToGrid(newWidth);
    newHeight = snapToGrid(newHeight);
    newX = snapToGrid(newX);
    newY = snapToGrid(newY);

    // Update size and position
    if (onSizeChange) {
      onSizeChange(element.id, {
        width: newWidth,
        height: newHeight,
        position_x: newX,
        position_y: newY,
      });
    }
  }, [isResizing, resizeStart, zoom, config, snapToGrid, onSizeChange, element.id]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    setResizeStart(null);
  }, []);

  // Global mouse event listeners for resize
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  return (
    <div
      ref={(node) => {
        drag(node);
        elementRef.current = node;
      }}
      className="absolute cursor-move"
      style={{
        left: element.position_x * zoom,
        top: element.position_y * zoom,
        width: width * zoom,
        height: height * zoom,
        transform: element.rotation ? `rotate(${element.rotation}deg)` : undefined,
        zIndex: isSelected ? 10 : 1,
        opacity: isDragging ? 0 : 1,
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (onClick) onClick(e);
      }}
      data-testid={`venue-element-${element.id}`}
    >
      {/* Element Shape */}
      <div
        className={`
          w-full h-full
          border-2 border-slate-300
          flex items-center justify-center
          transition-colors duration-150
          ${isSelected ? 'border-[#D4AF37] bg-[#D4AF37]/10' : 'bg-transparent hover:border-slate-200'}
        `}
        style={{
          borderRadius: isCircle ? '50%' : '2px',
        }}
      >
        <span
          className="text-slate-300 font-semibold text-center px-1 select-none"
          style={{
            fontSize: Math.max(8, Math.min(14, width * zoom * 0.1)) + 'px',
          }}
        >
          {element.label}
        </span>
      </div>

      {/* Resize Handles (only when selected) */}
      {isSelected && !isCircle && (
        <>
          <ResizeHandle position="nw" onMouseDown={handleResizeStart} />
          <ResizeHandle position="n" onMouseDown={handleResizeStart} />
          <ResizeHandle position="ne" onMouseDown={handleResizeStart} />
          <ResizeHandle position="w" onMouseDown={handleResizeStart} />
          <ResizeHandle position="e" onMouseDown={handleResizeStart} />
          <ResizeHandle position="sw" onMouseDown={handleResizeStart} />
          <ResizeHandle position="s" onMouseDown={handleResizeStart} />
          <ResizeHandle position="se" onMouseDown={handleResizeStart} />
        </>
      )}

      {/* Resize handles for circle (corner only) */}
      {isSelected && isCircle && (
        <>
          <ResizeHandle position="nw" onMouseDown={handleResizeStart} />
          <ResizeHandle position="ne" onMouseDown={handleResizeStart} />
          <ResizeHandle position="sw" onMouseDown={handleResizeStart} />
          <ResizeHandle position="se" onMouseDown={handleResizeStart} />
        </>
      )}

      {/* Rotation Handle (only when selected) */}
      {isSelected && (
        <div
          className="absolute flex flex-col items-center"
          style={{
            left: '50%',
            top: -30 * zoom,
            transform: 'translateX(-50%)',
            zIndex: 25,
          }}
        >
          {/* Line connecting to shape */}
          <div 
            className="w-px bg-[#D4AF37]"
            style={{ height: 15 * zoom }}
          />
          {/* Rotation handle circle */}
          <div
            className="w-4 h-4 rounded-full bg-[#D4AF37] border-2 border-white cursor-grab active:cursor-grabbing hover:bg-[#B5952F] transition-colors shadow-md"
            style={{
              marginTop: -2,
            }}
            onMouseDown={handleRotateStart}
            title="Drag to rotate (hold Shift for 15° snap)"
          />
        </div>
      )}

      {/* Floating Action Buttons (only when selected) */}
      {isSelected && (
        <>
          {/* Duplicate button - top right */}
          {onDuplicate && (
            <button
              className="absolute flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white shadow-lg transition-all hover:scale-110 z-30"
              style={{
                top: -10,
                right: -10,
              }}
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate(element);
              }}
              title="Duplicate"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            </button>
          )}

          {/* Delete button - bottom right */}
          {onDelete && (
            <button
              className="absolute flex items-center justify-center w-6 h-6 rounded-full bg-red-500 hover:bg-red-400 text-white shadow-lg transition-all hover:scale-110 z-30"
              style={{
                bottom: -10,
                right: -10,
              }}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(element.id);
              }}
              title="Delete"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18"></path>
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
              </svg>
            </button>
          )}
        </>
      )}
    </div>
  );
}
